import { FC, useEffect, useState } from "react";

import { DonationFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { trpc } from "../../../../../../common/helpers/trpc";
import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { useBuildPrefilledDescription } from "../../../common/prefillDataBuilders/useBuildPrefilledDescription";
import { useUpdateDonationDataInPreview } from "../../../common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { useFormSaveAndNavigate } from "../../useFormSaveAndNavigate";

import { LiveEditorStepDescriptionPoweredByAIStateless } from "./LiveEditorStepDescriptionPoweredByAI.stateless";
import { useDescriptionGenerationPolling } from "./useDescriptionGenerationPolling";

import { useAIFeaturesEnabled } from "@/features/LiveFormEditor/Steps/helpers/useAIFeaturesEnabled";
import { StepDescriptionFormData } from "@/features/LiveFormEditor/Steps/StepDescription/shared/types";

export const DonationStepDescriptionPoweredByAI: FC = () => {
  const {
    apiFormObject: apiDonationForm,
    formCategory,
    optionalFundraiserToken,
    isCreatedFromDuplicate,
  } = useLiveFormEditorContext<"donation-form">();
  const { t } = useTranslate();

  const localizedDonationFormField = apiDonationForm.donationFormFields?.find(
    (field) => field.locale === apiDonationForm.locale
  );
  const initialDescription = localizedDonationFormField?.description || null;

  const { buildPrefilledDescription } = useBuildPrefilledDescription();
  const prefilledText = buildPrefilledDescription({
    organizationName: apiDonationForm.organization?.name || "",
    isPeerToPeerCampaign: false,
  });

  const defaultValues: StepDescriptionFormData = {
    description: initialDescription,
  };

  const form = useForm<StepDescriptionFormData>({ defaultValues });

  const [isGenerating, setIsGenerating] = useState(false);
  const [aiGeneratedDescription, setAiGeneratedDescription] = useState<string | null>(null);

  const { pollDescriptionGeneration } = useDescriptionGenerationPolling(apiDonationForm.id);

  useEffect(() => {
    if (initialDescription) {
      return;
    }

    let mounted = true;

    const fetchDescription = async () => {
      setIsGenerating(true);
      const { descriptionStatus, description } = await pollDescriptionGeneration();

      if (!mounted) {
        return;
      }

      if (descriptionStatus === "completed" && description) {
        setAiGeneratedDescription(description);
        form.setValue("description", description, { shouldDirty: true });
      } else if (descriptionStatus === "failed") {
        // Fallback: keep the initial description (null) which shows the prefilled placeholder
        // The prefilledText is already available and will be used in the preview
        setAiGeneratedDescription(null);
      }

      setIsGenerating(false);
    };

    fetchDescription();

    return () => {
      mounted = false;
    };
  }, [apiDonationForm.id, form, initialDescription, pollDescriptionGeneration]);

  const { mutateAsync: updateDescription } = trpc.updateDonationFormDescription.useMutation();
  const startThankYouEmailGenerationWithAIMutation = trpc.startThankYouEmailGenerationWithAI.useMutation();

  const saveData = async (formData: StepDescriptionFormData) => {
    return updateDescription({
      donationFormId: apiDonationForm.id,
      description: formData.description,
      fundraiserToken: optionalFundraiserToken,
    });
  };

  const { isAIFeaturesEnabled } = useAIFeaturesEnabled();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepDescriptionFormData>({
    type: "donation-form",
    form,
    alwaysSaveOnNext: true,
    saveData,
    onFirstNextCompleted: () => {
      if (!isAIFeaturesEnabled) {
        return;
      }
      if (isCreatedFromDuplicate) {
        return;
      }

      const title = localizedDonationFormField?.title || "";
      const description = form.getValues("description");

      startThankYouEmailGenerationWithAIMutation.mutateAsync({
        formId: apiDonationForm.id,
        title,
        description,
        campaignDate: null,
        campaignLocation: null,
        formCategory,
        locale: apiDonationForm.locale,
      });
    },
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      const descriptionInPreview = formData.description ?? prefilledText;
      liveDataToMutate.donation.donationFormFields![0].description = descriptionInPreview;
      liveDataToMutate.donation.donationFormFields![0].sanitizedDescription = descriptionInPreview;
      liveDataToMutate.placeholders.isDescription = !formData.description;
    },
  });

  const stepTitle =
    apiDonationForm.category === DonationFormCategory.PeerToPeerCampaign
      ? t("dashboard", "liveFormEditor.stepDescription.p2p.title")
      : t("dashboard", "liveFormEditor.stepDescription.donationForm.title");

  return (
    <LiveEditorStepDescriptionPoweredByAIStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      initialDescription={initialDescription}
      prefilledText={prefilledText}
      isGenerating={isGenerating}
      aiGeneratedDescription={aiGeneratedDescription}
      wording={{
        stepTitle,
        stepDescription: t("dashboard", "liveFormEditor.stepDescription.donationForm.description"),
      }}
    />
  );
};
